import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, FileText, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";
import { PRESET_TEMPLATES, REPORT_BLOCKS, type BlockType } from "@/lib/reports/block-types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Template {
  id: string;
  name: string;
  description: string | null;
  blocks: Array<{ type: string }>;
  created_at: string;
  updated_at: string;
}

export default function ReportsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgId } = useTenant();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["report-templates", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("report_templates")
        .select("id, name, description, blocks, created_at, updated_at")
        .eq("org_id", orgId!)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return (data || []) as Template[];
    },
    enabled: !!orgId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("report_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Template deleted");
      queryClient.invalidateQueries({ queryKey: ["report-templates"] });
      setDeleteId(null);
    },
    onError: () => toast.error("Failed to delete template"),
  });

  return (
    <div className="flex-1 space-y-6 px-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">Create and manage client-ready portfolio reports</p>
        </div>
        <Button onClick={() => navigate("/portfolio/reports/builder")}>
          <Plus className="h-4 w-4 mr-1" />
          New Report
        </Button>
      </div>

      {/* Preset templates */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Quick Start Templates</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PRESET_TEMPLATES.map((preset, idx) => (
            <Card
              key={idx}
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => navigate("/portfolio/reports/builder")}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {preset.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-2">{preset.description}</p>
                <div className="flex flex-wrap gap-1">
                  {preset.blocks.slice(0, 4).map((type) => (
                    <Badge key={type} variant="outline" className="text-[10px]">
                      {REPORT_BLOCKS[type]?.name || type}
                    </Badge>
                  ))}
                  {preset.blocks.length > 4 && (
                    <Badge variant="outline" className="text-[10px]">
                      +{preset.blocks.length - 4} more
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Saved templates */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">Saved Templates</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No saved templates yet.</p>
              <p className="text-xs mt-1">Create a report and save it as a template for reuse.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => navigate(`/portfolio/reports/builder?template=${template.id}`)}
              >
                <CardHeader className="pb-2 flex flex-row items-start justify-between">
                  <CardTitle className="text-sm">{template.name}</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); setDeleteId(template.id); }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-xs text-muted-foreground mb-2">{template.description}</p>
                  )}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{template.blocks.length} blocks</span>
                    <span>{new Date(template.updated_at).toLocaleDateString()}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this template? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
